import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  newClientRequestId, videoTestConfig, videoTestSend,
  type VideoTestConfigResp, type VideoTestSendResp,
} from '@lib/care-api';
import { useClient, useFeatures, useFeaturesSettled } from '@lib/clients';
import { NOTE_MAX, VIDEO_TEST_WORDING as W, checkNote, composeVideoText, normalizedNoteLength } from '@lib/video-text';

/**
 * Video (test), nav 14 - ROW 410, William's word of 19 Sep 2026: "an interface in Care that allows me
 * - temporary and to test - to send a video to Sherry, myself, Alex and write a small note."
 *
 * DARK behind MEMBERCARE_VIDEO_LINKS and the test list (approval 208). The shell shows the tab only
 * when the sign-in read said the flag is on AND this operator is on the list; this page asks the same
 * two questions again and refuses to draw its form otherwise, because a URL typed by hand must not
 * reach a form the nav would not have offered.
 *
 * What the page does, in the order the operator reads it: pick a recipient (name and number, from the
 * list, never typed), pick the video, write a one-line note under a live counter (the platform's own
 * note rule, refused by name under the box), read the EXACT text that will go (the platform's own
 * template, composed locally by the same rule as the API, pinned in lockstep), press Send. The
 * answer's own sentence is printed as the banner, whatever the answer was: sent, a replay, the
 * platform's suppressed / queued / rejected, already sent today, the brake, the allowlist, the secret,
 * or the platform's video links switched off. The Sent today table is re-read after every press.
 *
 * THE REQUEST ID. Minted here per press and KEPT when the answer says retry:'same-id' (the brake, a
 * missing route, a lost answer), so pressing Send again replays rather than sends twice; a definite
 * answer (sent, or a refusal by name) drops it and the next press mints a fresh one.
 */

type Tone = 'ok' | 'warn' | 'error';
const TONE_CLASS: Record<Tone, string> = {
  ok: 'sms-notice sms-notice-ok',
  warn: 'sms-notice sms-notice-warn',
  error: 'sms-notice sms-notice-error',
};

function toneOf(ok: boolean, status: number, d: VideoTestSendResp): Tone {
  if (ok) return d.outcome === 'sent' && !d.replay ? 'ok' : 'warn';
  if (status === 429) return 'warn';
  return 'error';
}

export function VideoTest(): JSX.Element {
  const features = useFeatures();
  const settled = useFeaturesSettled();
  const client = useClient();
  const enabled = settled && features.videoLinks !== 'off' && features.videoTestAllowed;

  const [config, setConfig] = useState<VideoTestConfigResp | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [recipient, setRecipient] = useState('');
  const [test, setTest] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  /** The id of a press whose answer said "same id": kept until a definite answer arrives. */
  const [pendingId, setPendingId] = useState('');
  const [banner, setBanner] = useState<{ tone: Tone; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoadErr('');
    try {
      setConfig(await videoTestConfig());
    } catch (e) {
      setLoadErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    if (enabled) void load();
  }, [enabled, load, client]);

  if (!settled) {
    return <Frame><div className="muted" style={{ padding: '20px 0' }}>{W.loading}</div></Frame>;
  }
  if (!enabled) {
    return <Frame><div className="note warn" role="status" data-testid="video-test-not-enabled">{W.notEnabled}</div></Frame>;
  }

  const video = config?.videos.find((v) => v.test === test) ?? null;
  const noteCheck = checkNote(note);
  const noteLen = normalizedNoteLength(note);
  const previewText = video ? composeVideoText(video, noteCheck.ok ? noteCheck.note : '') : '';
  const canSend = !!config && !!recipient && !!video && noteCheck.ok && !sending;

  const press = async () => {
    if (!canSend || !video) return;
    const id = pendingId || newClientRequestId();
    setSending(true);
    setBanner(null);
    try {
      const res = await videoTestSend({ recipient, test: video.test, language: 'en', note: note.trim(), clientRequestId: id });
      const d = res.data as VideoTestSendResp;
      setPendingId(d.retry === 'same-id' ? id : '');
      setBanner({ tone: toneOf(res.ok, res.status, d), text: d.message || `API ${res.status}` });
    } catch {
      // No answer at all: the id is kept, so the next press replays rather than sends twice.
      setPendingId(id);
      setBanner({ tone: 'error', text: W.noAnswer });
    } finally {
      setSending(false);
      void load();
    }
  };

  const labelFor = (t: string): string => config?.videos.find((v) => v.test === t)?.label ?? t;

  return (
    <Frame>
      {loadErr && <div className="note danger" role="status" data-testid="video-test-load-error">{W.configFailed(loadErr)}</div>}
      {config && !config.platformConfigured && (
        <div className="note warn" role="status" data-testid="video-platform-not-configured">{W.platformNotConfigured}</div>
      )}
      {config && (
        <div className="video-test-form" data-testid="video-test-form">
          <div className="video-test-choices">
            <fieldset className="field">
              <legend className="field-label">{W.recipients}</legend>
              {config.recipients.length === 0 && <div className="muted">{W.noRecipients}</div>}
              {config.recipients.map((r) => (
                <label key={r.key} className={r.key === recipient ? 'video-choice on' : 'video-choice'}>
                  <input
                    type="radio"
                    name="video-recipient"
                    value={r.key}
                    checked={r.key === recipient}
                    onChange={() => setRecipient(r.key)}
                    data-testid={`video-recipient-${r.key}`}
                  />
                  <span>{r.name}</span>
                  <span className="muted mono">{r.phoneDisplay}</span>
                </label>
              ))}
            </fieldset>
            <fieldset className="field">
              <legend className="field-label">{W.videos}</legend>
              {config.videos.map((v) => (
                <label key={v.test} className={v.test === test ? 'video-choice on' : 'video-choice'}>
                  <input
                    type="radio"
                    name="video-test"
                    value={v.test}
                    checked={v.test === test}
                    onChange={() => setTest(v.test)}
                    data-testid={`video-kit-${v.test}`}
                  />
                  <span>{v.label}</span>
                  <span className="muted mono">{v.language}</span>
                </label>
              ))}
            </fieldset>
          </div>

          <div className="field">
            <label className="field-label" htmlFor="video-note">{W.note}</label>
            <div className="video-note-row">
              <input
                id="video-note"
                className="text-input"
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="A short note the text carries after the link"
                aria-describedby="video-note-counter"
              />
              <span
                id="video-note-counter"
                className={noteLen > NOTE_MAX ? 'video-note-counter over' : 'video-note-counter'}
                data-testid="video-note-counter"
              >
                {W.counter(noteLen)}
              </span>
            </div>
            {!noteCheck.ok && noteLen > 0 && <div className="micro" style={{ color: 'var(--crimson)', marginTop: 4 }}>{noteCheck.message}</div>}
          </div>

          <div className="field">
            <div className="field-label">{W.preview}</div>
            <div className="video-preview" data-testid="video-preview">{previewText || <span className="muted">{W.previewPick}</span>}</div>
          </div>

          <div className="video-test-actions">
            <button type="button" className="btn btn-primary" disabled={!canSend} onClick={() => void press()} data-testid="video-send">
              {sending ? W.sending : W.send}
            </button>
            {banner && <div role="status" className={TONE_CLASS[banner.tone]} data-testid="video-outcome">{banner.text}</div>}
          </div>

          <section className="video-sent" aria-label={W.sentToday}>
            <div className="field-label">{W.sentToday}</div>
            {config.ledger === 'unavailable' && <div className="note warn" role="status">{W.ledgerUnavailable}</div>}
            {config.sentToday.length === 0 ? (
              <div className="muted" data-testid="video-sent-empty">{W.nothingToday}</div>
            ) : (
              <table className="dt video-sent-table" data-testid="video-sent-today">
                <thead>
                  <tr>
                    <th>{W.columns.time}</th>
                    <th>{W.columns.to}</th>
                    <th>{W.columns.video}</th>
                    <th>{W.columns.link}</th>
                    <th>{W.columns.note}</th>
                    <th>{W.columns.outcome}</th>
                  </tr>
                </thead>
                <tbody>
                  {config.sentToday.map((r) => (
                    <tr key={r.clientRequestId}>
                      <td className="mono">{r.timePacific}</td>
                      <td>{r.recipientName}</td>
                      <td>{labelFor(r.test)}</td>
                      <td className="video-sent-link">{r.link ? <a href={r.link} target="_blank" rel="noopener noreferrer">{r.link}</a> : <span className="muted">-</span>}</td>
                      <td className="video-sent-note">{r.note}</td>
                      <td>{(W.outcome[r.outcome] ?? r.outcome) + (r.replay ? ` ${W.replayMark}` : '')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>
      )}
    </Frame>
  );
}

function Frame({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="page-pad">
      <header>
        <div className="section-eyebrow">{W.eyebrow}</div>
        <h1 className="section-title">{W.title}</h1>
        <div className="section-rule" />
        <p className="section-lede">{W.lede}</p>
      </header>
      {children}
    </div>
  );
}
